import fetchApi from "./fetchApi";

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(message, options = {}) {
    super(message);
    this.name = "ApiError";
    this.code = options.code;
    this.status = options.status;
    this.validationErrors = options.validationErrors;
    this.response = options.response;
  }

  isValidationError() {
    return this.code === 422;
  }

  isAuthError() {
    return this.code === 401 || this.status === 401;
  }
}

/**
 * Extracts validation error messages from API response
 */
function getValidationMessage(detail) {
  if (!detail) return null;

  if (Array.isArray(detail)) {
    return detail.map((err) => err.msg || err.message).join(", ");
  }

  if (typeof detail === "object") {
    return Object.values(detail).flat().join(", ");
  }

  return detail.toString();
}

/**
 * Creates a request function with consistent error handling
 *
 * @param {Object} config - Request configuration
 * @param {string} config.url - API endpoint URL
 * @param {string} config.method - HTTP method (GET, POST, PATCH, DELETE, etc.)
 * @param {Object} config.body - Request body
 * @param {Object} config.headers - Request headers
 * @param {boolean} config.isFormData - Whether body is FormData
 * @param {boolean} config.throwOnError - Whether to throw on error (default: true)
 * @param {Function} config.onSuccess - Success callback
 * @param {Function} config.onValidationError - Validation error callback
 * @returns {Promise} - Resolves with data or throws ApiError
 */
export async function createApiRequest({
  url,
  method = "GET",
  body = {},
  headers,
  isFormData = false,
  throwOnError = true,
  onSuccess,
  onValidationError,
}) {
  try {
    const response = await fetchApi[method.toLowerCase()](
      url,
      body,
      isFormData,
      headers,
    );

    const { status, message, data, code, detail } = response;

    // Success case
    if (status === "success") {
      onSuccess?.(data, message);
      return data;
    }

    // Error case
    if (status === "error") {
      // Handle validation errors (422)
      if (code === 422) {
        const validationMessage = getValidationMessage(detail);
        onValidationError?.(detail);

        if (throwOnError) {
          throw new ApiError(
            validationMessage || message || "Validation failed",
            {
              code,
              status,
              validationErrors: detail,
              response,
            },
          );
        }
        return null;
      }

      // Handle other errors
      if (throwOnError) {
        throw new ApiError(message || "Something went wrong", {
          code,
          status,
          response,
        });
      }
      return null;
    }

    // Unknown response format
    if (throwOnError) {
      throw new ApiError("Invalid response format", { response });
    }
    return null;
  } catch (error) {
    // Re-throw ApiError instances
    if (error instanceof ApiError) {
      throw error;
    }

    // // Check if error has API response (axios error)
    const apiResponse = error.response?.data;
    if (apiResponse) {
      const { status, message, code, detail } = apiResponse;

      // Handle validation errors from axios error
      if (code === 422 && status === "error") {
        const validationMessage = getValidationMessage(detail);
        onValidationError?.(detail);

        if (throwOnError) {
          throw new ApiError(
            validationMessage || message || "Validation failed",
            {
              code,
              status,
              validationErrors: detail,
              response: apiResponse,
            },
          );
        }
        return null;
      }

      // Handle other API errors from axios error
      if (status === "error" && throwOnError) {
        throw new ApiError(message || "Something went wrong", {
          code,
          status: error?.response?.status,
          response: apiResponse,
        });
      }
    }

    // Network or unexpected errors
    if (throwOnError) {
      throw new ApiError(error.message || "Request failed", {
        code: error.code,
        status: error?.response?.status,
      });
    }
    return null;
  }
}

/**
 * Creates a service layer helper function
 * Returns data directly and throws on error by default
 *
 * Usage:
 * const service = createApiService();
 *
 * // Throwing mode (default) - works with showToast.promise
 * const data = await service.request({ url, method, body });
 *
 * // Non-throwing mode - for manual error handling
 * const data = await service.request({ url, method, body, throwOnError: false });
 * if (!data) {
 *   // handle error
 * }
 */
export function createApiService() {
  return {
    request: createApiRequest,
  };
}
